/**
 * registerSW — Progressive Web App service-worker registration.
 *
 * Uses `workbox-window` (via vite-plugin-pwa's virtual module) to register
 * the generated `sw.js` and show a lightweight "Update available" banner
 * injected outside the React tree when a new version is waiting.
 *
 * The actual SW strategy (precache shell + NetworkFirst for artifacts) is
 * declared in vite.config.ts via `vite-plugin-pwa`.
 *
 * Update flow:
 *   1. A new SW is downloaded and waiting to activate.
 *   2. This module injects a banner div into <body>.
 *   3. The user clicks "Reload" → `updateSW(true)` → SW skips-waiting,
 *      clients claim, and the page reloads with the new version active.
 */

import { registerSW } from 'virtual:pwa-register';

// ── Banner UI (outside React tree — avoids touching apps/viewer/src/ui/) ──

function createUpdateBanner(onReload: () => void): HTMLElement {
  const banner = document.createElement('div');
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');
  banner.style.cssText = [
    'position:fixed',
    'bottom:env(safe-area-inset-bottom,0px)',
    'left:0',
    'right:0',
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'gap:0.75rem',
    'padding:0.875rem 1.25rem',
    'background:#12121c',
    'border-top:1px solid #2a2a3a',
    'color:#e8e8f0',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    'font-size:0.9375rem',
    'z-index:9999',
  ].join(';');

  const text = document.createElement('span');
  text.textContent = 'An update is available.';

  const btn = document.createElement('button');
  btn.textContent = 'Reload';
  btn.style.cssText = [
    'min-height:44px',
    'padding:0.5rem 1rem',
    'background:#0d9488',
    'color:#fff',
    'border:none',
    'border-radius:6px',
    'font-size:0.9375rem',
    'font-weight:600',
    'cursor:pointer',
    'flex-shrink:0',
  ].join(';');
  btn.addEventListener('click', onReload);

  banner.appendChild(text);
  banner.appendChild(btn);
  return banner;
}

// ── Registration ──────────────────────────────────────────────────────────

let banner: HTMLElement | null = null;

const updateSW = registerSW({
  onNeedRefresh() {
    if (banner) return; // already shown
    banner = createUpdateBanner(() => {
      updateSW(true).catch(() => {
        // If skipWaiting fails, a manual reload still picks up the new SW on next navigation
        window.location.reload();
      });
    });
    document.body.appendChild(banner);
  },

  onOfflineReady() {
    if (import.meta.env.DEV) {
      console.info('[pwa] App is ready for offline use.');
    }
  },

  onRegisterError(error: unknown) {
    if (import.meta.env.DEV) {
      console.error('[pwa] Service-worker registration failed', error);
    }
  },
});
